import deriveIframeOriginFromRootPageOrigin from '../utils/deriveIframeOriginFromRootPageOrigin.js';
import deriveRootLayoutCssTextFromProps from '../utils/deriveRootLayoutCssTextFromProps.js';
import webZephrIframePropsMergeTopPageMetadata from '../utils/webZephrIframePropsMergeTopPageMetadata.js';
import waitForBouncex from '../utils/waitForBouncex.js';
import isComponentCriticalUserExperiencePath from '../utils/isComponentCriticalUserExperiencePath.js';

const RENDER_INLINE_CAMPAIGN_AFTER_PARAGRAPH_NUMBER = 8;

async function onZephrInitializeComponent(e) {
  const { detail = {} } = e;

  const { urlSearchParams: urlSearchParamsProp, componentName } = detail;

  if (__DEV__) {
    console.log('Web.Zephr:event: onZephrInitializeComponent', e);
  }

  const isNonCriticalPathComponent =
    !isComponentCriticalUserExperiencePath(componentName);
  if (isNonCriticalPathComponent) {
    await waitForBouncex();

    const shouldSkipDueToWunderkindCampaignConflict =
      document.querySelector('[id^=bx-campaign]') ||
      (window.bouncex && window.bouncex.campaigns);
    if (shouldSkipDueToWunderkindCampaignConflict) {
      console.info(
        'Web.Zephr:event:onZephrInitializeComponent: exit early due to presence of conflicting wunderkind campaign (e.g., [id^=bx-campaign])',
      );
      return;
    }
  }

  if (!componentName) {
    return console.error('Web.Zephr:Error: componentName is required', detail);
  }

  const isInlineComponent = /^inline/i.test(componentName);
  const isGateComponent = /^gate/i.test(componentName);
  const isTopBarComponent = /^topbar/i.test(componentName);
  const isPageComponent = /^page/i.test(componentName);

  function getTargetElementSelector() {
    if (isInlineComponent || isGateComponent) {
      return '.Article .group p';
    }

    if (isPageComponent) {
      return '.zephr-page';
    }

    return 'body';
  }

  const targetElementSelector = getTargetElementSelector();

  function getTargetElement() {
    let targetElement;
    if (isInlineComponent) {
      targetElement = document.querySelectorAll(targetElementSelector)[
        RENDER_INLINE_CAMPAIGN_AFTER_PARAGRAPH_NUMBER
      ];
    } else if (isGateComponent) {
      targetElement = [
        ...document.querySelectorAll(targetElementSelector),
      ].pop();
    } else {
      targetElement = document.querySelector(targetElementSelector);
    }

    if (!targetElement) {
      console.warn(
        `Web.Zephr:Warn: onZephrInitializeComponent event target element document.querySelector('${targetElementSelector}') does not exist.`,
        detail,
      );
    }

    return targetElement;
  }

  if (!getTargetElement()) {
    return;
  }

  const {
    minHeight,
    maxHeight,
    minWidth,
    maxWidth,
    position,
    bottom,
    top,
    left,
    right,
    display = 'none',
    trigger: triggerProp = null,
    intent = null,
    showDelayMilliseconds,

    ...webZephrIframeProps
  } = urlSearchParamsProp;

  const iframe = document.createElement('iframe');
  iframe.allowTransparency = true;

  const layoutProps = {
    minHeight,
    maxHeight,
    minWidth,
    maxWidth,
    position,
    bottom,
    top,
    left,
    right,
    display,
  };

  iframe.style.cssText = deriveRootLayoutCssTextFromProps(layoutProps);

  // Used by integration.css
  iframe.className = `zephrIframeOutcome ${componentName}`;

  iframe.sandbox = [
    'allow-downloads', // download pdf
    'allow-forms', // submit email
    'allow-scripts', // render app
    'allow-top-navigation', // navigate to landing pages
    'allow-same-origin', // allow access to cookies and local storage for analytics $t
    'allow-popups', // allow popups links that use _blank
  ].join(' ');

  const origin = deriveIframeOriginFromRootPageOrigin();

  const webZephrIframeUrlSearchParams = new URLSearchParams(
    webZephrIframePropsMergeTopPageMetadata(webZephrIframeProps),
  );

  const src = `${origin}/components/${componentName}?${webZephrIframeUrlSearchParams.toString()}`;
  iframe.src = src;

  const targetElement = getTargetElement();
  if (isInlineComponent || isGateComponent) {
    targetElement.insertAdjacentElement('afterend', iframe);
  } else if (isTopBarComponent) {
    targetElement.prepend(iframe);
  } else {
    targetElement.appendChild(iframe);
  }

  const trigger = triggerProp || intent; // backward-compatible with intent
  const selector = `[src^="${src}"]`;
  const options = {
    detail: {
      selector,
      showDelayMilliseconds,
    },
  };

  const eventType =
    trigger === 'exit' ? 'zephrobserveexittrigger' : 'onzephrshowcomponent';

  const event = new CustomEvent(eventType, options);

  return window.dispatchEvent(event);
}

window.addEventListener(
  'onzephrinitializecomponent',
  onZephrInitializeComponent,
);

export default onZephrInitializeComponent;
